import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, CalendarClock, Clock } from "lucide-react";

export default function Checkin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("loading"); // loading | success | already | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const done = useRef(false);

  useEffect(() => {
    if (user === undefined) return; // still checking session
    if (user === null) {
      navigate(`/login?next=${encodeURIComponent(`/checkin?token=${token}`)}`, { replace: true });
      return;
    }
    if (done.current) return;
    done.current = true;
    api.post("/attendance/checkin", { token })
      .then((r) => { setResult(r.data); setState(r.data.already ? "already" : "success"); })
      .catch((e) => { setError(formatApiError(e.response?.data?.detail)); setState("error"); });
  }, [user, token, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-950" data-testid="checkin-page">
      <div className="w-full max-w-sm text-center space-y-6">
        <div className="flex items-center justify-center gap-2.5">
          <img src="/kelderke-logo.png" alt="'t Kelderke" className="w-10 h-10 object-contain" />
          <p className="font-heading font-bold text-white text-lg">Bar 't Kelderke</p>
        </div>

        {state === "loading" && (
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 space-y-3" data-testid="checkin-loading">
            <Loader2 className="w-10 h-10 animate-spin text-red-600 mx-auto" />
            <p className="text-muted-foreground">Bezig met inchecken…</p>
          </div>
        )}

        {(state === "success" || state === "already") && (
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 space-y-4" data-testid="checkin-success">
            <CheckCircle2 className="w-16 h-16 text-red-600 mx-auto" />
            <div>
              <h1 className="font-heading text-2xl font-bold">
                {state === "already" ? "Al ingecheckt" : "Ingecheckt!"}
              </h1>
              <p className="text-muted-foreground mt-1">Hallo {result?.user_name}</p>
            </div>
            <div className="flex items-center justify-center gap-2 text-lg font-semibold">
              <Clock className="w-5 h-5 text-red-600" /> {result?.local_time}
            </div>
            {result?.scheduled ? (
              <div className="text-sm bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 rounded-lg px-3 py-2 flex items-center justify-center gap-2" data-testid="checkin-shift">
                <CalendarClock className="w-4 h-4" /> {result.service_name} · {result.service_time}
              </div>
            ) : (
              <div className="text-sm bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 rounded-lg px-3 py-2" data-testid="checkin-unscheduled">
                Je staat vandaag niet ingepland, maar je aanwezigheid is geregistreerd.
              </div>
            )}
            <Button variant="outline" className="w-full" onClick={() => navigate("/")} data-testid="checkin-home">
              Naar de app
            </Button>
          </div>
        )}

        {state === "error" && (
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 space-y-4" data-testid="checkin-error">
            <XCircle className="w-16 h-16 text-rose-600 mx-auto" />
            <h1 className="font-heading text-2xl font-bold">Inchecken mislukt</h1>
            <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</p>
            <Button variant="outline" className="w-full" onClick={() => navigate("/")}>Naar de app</Button>
          </div>
        )}
      </div>
    </div>
  );
}
